import { useEffect, useMemo, useRef, useState } from "react";
import {
    Upload, Play, Pause, Scissors, Trash2, Download, Volume2, VolumeX,
    Music, Image as ImageIcon, Type, Loader2, RotateCw, Plus,
    Captions, Sparkles, Mic, Layers,
} from "lucide-react";
import { api, API } from "../lib/api";
import { toast } from "sonner";

/**
 * Video Editor — timeline-based editor backed by server-side FFmpeg.
 *
 * Clips: each clip is a {input_id, start, end, speed, volume, fade_in, fade_out, rotate_deg}.
 * Splitting a clip inserts a second clip at the playhead.
 * Audio track: optional background music (uploaded), per-track volume + mute-video toggle.
 * Text overlays: per-overlay text, position, time range.
 * Logo: single image overlay.
 *
 * On Render:
 * 1. POST /api/video/render with the full spec → returns {job_id}
 * 2. Poll /api/video/jobs/{job_id} until status=done|failed
 * 3. Trigger /api/video/download/{job_id} for the user to save the file.
 */

const ASPECTS = [
    { key: "16:9", label: "16:9 Landscape" },
    { key: "9:16", label: "9:16 Vertical (TikTok/Reels)" },
    { key: "1:1", label: "1:1 Square" },
    { key: "4:5", label: "4:5 Portrait" },
];

const RES_OPTIONS = ["480p", "720p", "1080p", "original"];

export default function VideoEditor() {
    const previewRef = useRef(null);
    const timelineRef = useRef(null);

    const [inputs, setInputs] = useState([]);      // {id, path, filename, duration?}
    const [clips, setClips] = useState([]);        // {id, input_id, start, end, speed, volume, fade_in, fade_out, rotate_deg}
    const [audioTracks, setAudioTracks] = useState([]); // [{id, path, filename, volume, start_offset, fade_in, fade_out}]
    const [muteVideoAudio, setMuteVideoAudio] = useState(false);
    const [logo, setLogo] = useState(null);        // {path, filename, x, y, scale}
    const [textOverlays, setTextOverlays] = useState([]); // {id, text, x, y, fontsize, color, start, end}
    const [resolution, setResolution] = useState("1080p");
    const [aspect, setAspect] = useState("16:9");
    const [selectedClipId, setSelectedClipId] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [renderJob, setRenderJob] = useState(null); // {job_id, status, error}
    const [batchJobs, setBatchJobs] = useState(null); // [{job_id, aspect_ratio, status?}]
    const [uploading, setUploading] = useState(false);

    // Phase 2 AI state
    const [aiBusy, setAiBusy] = useState(""); // 'transcribe' | 'silences' | 'highlights' | ''
    const [transcript, setTranscript] = useState(null); // {segments, srt_path, text, segment_count}
    const [autoSubs, setAutoSubs] = useState(false);
    const [silences, setSilences] = useState(null); // [{start,end,duration}]
    const [highlights, setHighlights] = useState(null); // [{start,end,title,reason}]

    // Derived: a single "preview" — we just play the currently-selected clip in HTML5.
    const previewInput = useMemo(() => {
        if (!selectedClipId) return clips.length ? inputs.find(i => i.id === clips[0].input_id) : null;
        const clip = clips.find(c => c.id === selectedClipId);
        return clip ? inputs.find(i => i.id === clip.input_id) : null;
    }, [selectedClipId, clips, inputs]);
    const previewUrl = useMemo(() => previewInput ? `${API}/video/preview?p=${encodeURIComponent(previewInput.path)}` : null, [previewInput]);

    // --- Upload helpers ------------------------------------------------------
    const uploadFile = async (file) => {
        const form = new FormData();
        form.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/video/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            return data;
        } finally { setUploading(false); }
    };

    const onUploadVideo = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        try {
            const up = await uploadFile(f);
            const inputId = `v${inputs.length + 1}`;
            const duration = await readDuration(f);
            setInputs((ins) => [...ins, { id: inputId, path: up.path, filename: f.name, duration }]);
            // Auto-add a clip covering the full file
            const clipId = `c${Date.now()}`;
            setClips((cs) => [...cs, {
                id: clipId, input_id: inputId, start: 0, end: duration || 60,
                speed: 1.0, volume: 1.0, fade_in: 0, fade_out: 0, rotate_deg: 0,
            }]);
            setSelectedClipId(clipId);
            toast.success("Video added");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        }
    };

    const onUploadAudio = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        try {
            const up = await uploadFile(f);
            setAudioTracks((ts) => [...ts, {
                id: `at${Date.now()}`, path: up.path, filename: f.name,
                volume: 0.5, start_offset: 0, fade_in: 0, fade_out: 0,
            }]);
            toast.success("Music track added");
        } catch (err) { toast.error("Upload failed"); }
        e.target.value = "";  // allow re-uploading the same file later
    };

    const onUploadLogo = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        try {
            const up = await uploadFile(f);
            setLogo({ path: up.path, filename: f.name, x: "W-w-30", y: "30", scale: 0.18 });
            toast.success("Logo added");
        } catch (err) { toast.error("Upload failed"); }
    };

    // --- Clip operations -----------------------------------------------------
    const split = () => {
        if (!selectedClipId) return;
        const clip = clips.find(c => c.id === selectedClipId);
        if (!clip) return;
        const v = previewRef.current;
        const t = v ? v.currentTime : (clip.start + (clip.end - clip.start) / 2);
        if (t <= clip.start + 0.1 || t >= clip.end - 0.1) {
            toast.error("Move the playhead inside the clip to split it.");
            return;
        }
        const a = { ...clip, end: t };
        const b = { ...clip, id: `c${Date.now()}`, start: t };
        setClips((cs) => cs.flatMap(c => c.id === selectedClipId ? [a, b] : [c]));
        toast.success("Clip split");
    };
    const removeClip = (id) => {
        setClips((cs) => cs.filter(c => c.id !== id));
        if (selectedClipId === id) setSelectedClipId(null);
    };
    const updateClip = (id, patch) => setClips((cs) => cs.map(c => c.id === id ? { ...c, ...patch } : c));

    // --- Text overlays -------------------------------------------------------
    const addTextOverlay = () => {
        setTextOverlays((ts) => [...ts, {
            id: `t${Date.now()}`, text: "Your title",
            x: "(w-tw)/2", y: "h-th-60", fontsize: 48, color: "white",
            start: 0, end: 5,
        }]);
    };
    const updateText = (id, patch) => setTextOverlays((ts) => ts.map(t => t.id === id ? { ...t, ...patch } : t));
    const removeText = (id) => setTextOverlays((ts) => ts.filter(t => t.id !== id));

    // --- Render --------------------------------------------------------------
    const buildSpec = (overrides = {}) => ({
        inputs: inputs.map(i => ({ id: i.id, path: i.path })),
        clips: clips.map(c => ({
            input_id: c.input_id, start: c.start, end: c.end, speed: c.speed,
            volume: c.volume, fade_in: c.fade_in, fade_out: c.fade_out, rotate_deg: c.rotate_deg,
        })),
        audio_path: null,
        audio_volume: 0.5,
        audio_tracks: audioTracks.map(t => ({
            path: t.path, volume: t.volume, fade_in: t.fade_in,
            fade_out: t.fade_out, start_offset: t.start_offset,
        })),
        mute_video_audio: muteVideoAudio,
        text_overlays: textOverlays.map(t => ({
            text: t.text, x: t.x, y: t.y, fontsize: t.fontsize, color: t.color,
            start: t.start, end: t.end,
        })),
        logo: logo ? { path: logo.path, x: logo.x, y: logo.y, scale: logo.scale } : null,
        resolution, aspect_ratio: aspect,
        subtitles_srt_path: autoSubs && transcript?.srt_path ? transcript.srt_path : null,
        ...overrides,
    });

    const render = async () => {
        if (clips.length === 0) { toast.error("Add at least one video clip first."); return; }
        try {
            const { data } = await api.post("/video/render", buildSpec());
            setRenderJob({ job_id: data.job_id, status: "queued" });
            setBatchJobs(null);
            toast.success(autoSubs ? "Rendering with auto-subtitles…" : "Rendering started…");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Render failed to start");
        }
    };

    const renderBatch = async () => {
        if (clips.length === 0) { toast.error("Add at least one video clip first."); return; }
        try {
            const { data } = await api.post("/video/render-batch", buildSpec());
            setBatchJobs(data.jobs.map(j => ({ ...j, status: "queued" })));
            setRenderJob(null);
            toast.success("Social pack queued (16:9 · 9:16 · 1:1)…");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Batch render failed to start");
        }
    };

    // --- Phase 2 AI handlers ------------------------------------------------
    const sourceForAI = useMemo(() => {
        // Use the first selected clip's source video; fall back to first input.
        if (selectedClipId) {
            const c = clips.find(x => x.id === selectedClipId);
            if (c) return inputs.find(i => i.id === c.input_id);
        }
        return inputs[0] || null;
    }, [selectedClipId, clips, inputs]);

    const runTranscribe = async () => {
        if (!sourceForAI) { toast.error("Add a video first."); return; }
        setAiBusy("transcribe");
        try {
            const { data } = await api.post("/video/transcribe", { path: sourceForAI.path });
            setTranscript(data);
            setAutoSubs(true);
            toast.success(`Transcribed (${data.segment_count} segments). Auto-subtitles ON.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Transcription failed");
        } finally { setAiBusy(""); }
    };

    const runSilenceDetect = async () => {
        if (!sourceForAI) { toast.error("Add a video first."); return; }
        setAiBusy("silences");
        try {
            const { data } = await api.post("/video/silences", {
                path: sourceForAI.path, threshold_db: -32, min_duration: 0.8,
            });
            setSilences(data.silences);
            toast.success(`${data.silences.length} silent gaps found.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silence detection failed");
        } finally { setAiBusy(""); }
    };

    const trimAllSilences = () => {
        if (!silences || silences.length === 0) { toast.error("No silences detected."); return; }
        // For each existing clip, replace it with a list of clips that skip every silent range
        // that overlaps the clip's [start, end] window.
        setClips((curr) => {
            const out = [];
            curr.forEach((c) => {
                const overlapping = silences
                    .filter(s => s.end > c.start && s.start < c.end)
                    .sort((a, b) => a.start - b.start);
                if (overlapping.length === 0) { out.push(c); return; }
                let cursor = c.start;
                overlapping.forEach((s, i) => {
                    if (s.start > cursor) {
                        out.push({ ...c, id: `c${Date.now()}_${out.length}`, start: cursor, end: Math.min(c.end, s.start) });
                    }
                    cursor = Math.max(cursor, s.end);
                });
                if (cursor < c.end) {
                    out.push({ ...c, id: `c${Date.now()}_${out.length}`, start: cursor, end: c.end });
                }
            });
            return out.filter(c => c.end - c.start > 0.05);
        });
        toast.success("Silences trimmed across all clips.");
    };

    const runHighlights = async () => {
        if (!transcript?.segments?.length) { toast.error("Run Transcribe first."); return; }
        setAiBusy("highlights");
        try {
            const { data } = await api.post("/video/highlights", {
                segments: transcript.segments, window_seconds: 30, n: 3,
            });
            setHighlights(data.highlights);
            toast.success(`${data.highlights.length} highlights found.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Highlight detection failed");
        } finally { setAiBusy(""); }
    };

    const applyHighlight = (h) => {
        if (!sourceForAI) return;
        const clipId = `c${Date.now()}`;
        setClips((cs) => [...cs, {
            id: clipId, input_id: sourceForAI.id,
            start: h.start, end: h.end,
            speed: 1.0, volume: 1.0, fade_in: 0.2, fade_out: 0.2, rotate_deg: 0,
        }]);
        setSelectedClipId(clipId);
        toast.success(`Added clip: "${h.title}"`);
    };


    // Poll job status
    useEffect(() => {
        if (!renderJob || ["done", "failed"].includes(renderJob.status)) return;
        const id = setInterval(async () => {
            try {
                const { data } = await api.get(`/video/jobs/${renderJob.job_id}`);
                setRenderJob(j => ({ ...j, ...data }));
                if (data.status === "done") { toast.success("Render complete"); clearInterval(id); }
                if (data.status === "failed") { toast.error("Render failed"); clearInterval(id); }
            } catch { /* ignore */ }
        }, 2000);
        return () => clearInterval(id);
    }, [renderJob?.job_id]);

    // Poll batch jobs
    useEffect(() => {
        if (!batchJobs || batchJobs.every(j => ["done", "failed"].includes(j.status))) return;
        const id = setInterval(async () => {
            const updated = await Promise.all(batchJobs.map(async (j) => {
                if (["done", "failed"].includes(j.status)) return j;
                try {
                    const { data } = await api.get(`/video/jobs/${j.job_id}`);
                    return { ...j, ...data };
                } catch { return j; }
            }));
            setBatchJobs(updated);
            if (updated.every(j => ["done", "failed"].includes(j.status))) {
                toast.success("Social pack complete");
                clearInterval(id);
            }
        }, 2500);
        return () => clearInterval(id);
    }, [batchJobs?.map(j => j.job_id).join("|")]);

    const downloadRender = async () => {
        if (!renderJob?.job_id) return;
        const r = await api.get(`/video/download/${renderJob.job_id}`, { responseType: "blob" });
        const blob = new Blob([r.data], { type: "video/mp4" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `vu-render.mp4`;
        a.click();
    };

    const downloadBatchJob = async (jobId, aspectKey) => {
        const r = await api.get(`/video/download/${jobId}`, { responseType: "blob" });
        const blob = new Blob([r.data], { type: "video/mp4" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `vu-${aspectKey.replace(":", "x")}.mp4`;
        a.click();
    };

    const selectedClip = clips.find(c => c.id === selectedClipId);
    const totalDuration = clips.reduce((s, c) => s + Math.max(0, (c.end - c.start) / Math.max(0.25, c.speed)), 0);

    return (
        <div className="space-y-4" data-testid="video-editor-v2">
            {/* Top toolbar */}
            <div className="card-warm p-3 flex flex-wrap items-center gap-2">
                <label className="btn-primary inline-flex items-center gap-2 cursor-pointer text-sm" data-testid="ve-upload-video">
                    {uploading ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
                    {uploading ? "Uploading…" : "Add video"}
                    <input type="file" accept="video/*" className="hidden" onChange={onUploadVideo}/>
                </label>
                <label className="btn-ghost inline-flex items-center gap-2 cursor-pointer text-sm" data-testid="ve-upload-audio">
                    <Music size={14}/> Music
                    <input type="file" accept="audio/*" className="hidden" onChange={onUploadAudio}/>
                </label>
                <label className="btn-ghost inline-flex items-center gap-2 cursor-pointer text-sm" data-testid="ve-upload-logo">
                    <ImageIcon size={14}/> Logo
                    <input type="file" accept="image/*" className="hidden" onChange={onUploadLogo}/>
                </label>
                <button onClick={addTextOverlay} className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="ve-add-text">
                    <Type size={14}/> Text
                </button>
                <div className="h-6 w-px bg-black/10 mx-1"/>
                <button onClick={split} disabled={!selectedClipId} className={`inline-flex items-center gap-2 text-sm ${selectedClipId ? "btn-ghost" : "btn-ghost opacity-40 cursor-not-allowed"}`} data-testid="ve-split">
                    <Scissors size={14}/> Split
                </button>
                <div className="h-6 w-px bg-black/10 mx-1"/>
                {/* Phase 2 AI tools */}
                <button onClick={runTranscribe} disabled={!sourceForAI || aiBusy === "transcribe"}
                        className={`inline-flex items-center gap-2 text-sm ${sourceForAI ? "btn-ghost" : "btn-ghost opacity-40 cursor-not-allowed"}`}
                        data-testid="ve-ai-transcribe">
                    {aiBusy === "transcribe" ? <Loader2 size={14} className="animate-spin"/> : <Mic size={14}/>}
                    Transcribe
                </button>
                <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer" data-testid="ve-autosubs-label">
                    <input type="checkbox" checked={autoSubs} disabled={!transcript}
                           onChange={(e) => setAutoSubs(e.target.checked)} data-testid="ve-autosubs-toggle"/>
                    <Captions size={14}/> Auto-subtitles
                </label>
                <button onClick={runSilenceDetect} disabled={!sourceForAI || aiBusy === "silences"}
                        className={`inline-flex items-center gap-2 text-sm ${sourceForAI ? "btn-ghost" : "btn-ghost opacity-40 cursor-not-allowed"}`}
                        data-testid="ve-ai-silences">
                    {aiBusy === "silences" ? <Loader2 size={14} className="animate-spin"/> : <VolumeX size={14}/>}
                    Find silences
                </button>
                <button onClick={runHighlights} disabled={!transcript?.segments?.length || aiBusy === "highlights"}
                        className={`inline-flex items-center gap-2 text-sm ${transcript?.segments?.length ? "btn-ghost" : "btn-ghost opacity-40 cursor-not-allowed"}`}
                        data-testid="ve-ai-highlights">
                    {aiBusy === "highlights" ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>}
                    Find highlights
                </button>
                <div className="ml-auto flex items-center gap-2">
                    <select value={aspect} onChange={(e) => setAspect(e.target.value)} className="text-xs btn-ghost" data-testid="ve-aspect">
                        {ASPECTS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                    </select>
                    <select value={resolution} onChange={(e) => setResolution(e.target.value)} className="text-xs btn-ghost" data-testid="ve-res">
                        {RES_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <button onClick={renderBatch} disabled={clips.length === 0 || (batchJobs && batchJobs.some(j => ["queued","running"].includes(j.status)))}
                            className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="ve-batch-render"
                            title="Render 16:9 + 9:16 + 1:1 at once">
                        <Layers size={12}/> Social pack
                    </button>
                    <button onClick={render} disabled={clips.length === 0 || (renderJob && ["queued", "running"].includes(renderJob.status))}
                            className="btn-primary text-xs inline-flex items-center gap-1.5" data-testid="ve-render">
                        {renderJob && ["queued", "running"].includes(renderJob.status)
                          ? <><Loader2 size={12} className="animate-spin"/> Rendering…</>
                          : <><Download size={12}/> Export</>}
                    </button>
                </div>
            </div>

            {/* Preview */}
            <div className="grid lg:grid-cols-12 gap-4">
                <div className="lg:col-span-8">
                    <div className="card-warm p-2 rounded-2xl">
                        <div className="rounded-xl bg-black overflow-hidden flex items-center justify-center mx-auto"
                             style={{ aspectRatio: aspect.replace(":", " / "), maxHeight: 480, maxWidth: "100%" }}
                             data-testid="ve-preview-box">
                            {previewUrl ? (
                                <video ref={previewRef} src={previewUrl} controls
                                       className="w-full h-full object-contain"
                                       style={{ transform: selectedClip ? `rotate(${selectedClip.rotate_deg || 0}deg)` : "none" }}
                                       data-testid="ve-preview-video"/>
                            ) : (
                                <div className="text-white/60 text-sm flex items-center gap-2">
                                    <ImageIcon size={16}/> Add a video to start
                                </div>
                            )}
                        </div>
                        <div className="text-[10px] text-[var(--text-ink)]/45 text-center mt-1.5">
                            Preview is letterboxed to match how <strong>{aspect}</strong> will render. Black bars are added by FFmpeg to preserve the original frame.
                        </div>
                    </div>
                    {renderJob && (
                        <RenderBanner job={renderJob} onDownload={downloadRender}/>
                    )}
                    {batchJobs && (
                        <div className="card-warm px-3 py-2 mt-3 space-y-1" data-testid="ve-batch-banner">
                            <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55 mb-1 inline-flex items-center gap-1">
                                <Layers size={12}/> Social pack
                            </div>
                            {batchJobs.map(j => (
                                <div key={j.job_id} className="flex items-center justify-between text-xs" data-testid={`ve-batch-${j.aspect_ratio.replace(":","x")}`}>
                                    <span className="inline-flex items-center gap-1.5">
                                        {["queued","running"].includes(j.status) && <Loader2 size={12} className="animate-spin"/>}
                                        <strong>{j.aspect_ratio}</strong>
                                        <span className="text-[var(--text-ink)]/55">
                                            {j.status === "done" ? "ready" : j.status === "failed" ? "failed" : j.status}
                                        </span>
                                    </span>
                                    {j.status === "done" && (
                                        <button onClick={() => downloadBatchJob(j.job_id, j.aspect_ratio)} className="btn-primary text-[10px] px-2 py-1 inline-flex items-center gap-1">
                                            <Download size={10}/> MP4
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Right inspector */}
                <div className="lg:col-span-4 space-y-3">
                    {selectedClip ? (
                        <ClipInspector clip={selectedClip} input={inputs.find(i => i.id === selectedClip.input_id)}
                                       onChange={(patch) => updateClip(selectedClip.id, patch)}
                                       onRemove={() => removeClip(selectedClip.id)}/>
                    ) : (
                        <div className="card-warm p-3 text-xs text-[var(--text-ink)]/55" data-testid="ve-no-clip">
                            Pick a clip in the timeline to edit trim, speed, fade and rotation.
                        </div>
                    )}
                    {audioTracks.length > 0 && (
                        <div className="card-warm p-3 space-y-3" data-testid="ve-audio-tracks">
                            <div className="flex items-center justify-between">
                                <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55 inline-flex items-center gap-1">
                                    <Music size={12}/> Audio tracks ({audioTracks.length})
                                </div>
                                <label className="text-[10px] inline-flex items-center gap-1.5 cursor-pointer" data-testid="ve-mute-original">
                                    <input type="checkbox" checked={muteVideoAudio} onChange={(e) => setMuteVideoAudio(e.target.checked)}/>
                                    Mute video
                                </label>
                            </div>
                            {audioTracks.map((t, i) => (
                                <AudioTrackRow key={t.id} index={i} t={t}
                                               onChange={(patch) => setAudioTracks((arr) => arr.map(x => x.id === t.id ? { ...x, ...patch } : x))}
                                               onRemove={() => setAudioTracks((arr) => arr.filter(x => x.id !== t.id))}/>
                            ))}
                        </div>
                    )}
                    {logo && (
                        <LogoInspector logo={logo} onChange={(patch) => setLogo(l => ({ ...l, ...patch }))} onRemove={() => setLogo(null)}/>
                    )}
                    {textOverlays.length > 0 && (
                        <div className="card-warm p-3 space-y-3" data-testid="ve-text-overlays">
                            <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55">Text overlays</div>
                            {textOverlays.map(t => (
                                <TextOverlayRow key={t.id} t={t} onChange={(patch) => updateText(t.id, patch)} onRemove={() => removeText(t.id)}/>
                            ))}
                        </div>
                    )}
                    {silences && silences.length > 0 && (
                        <div className="card-warm p-3 space-y-2" data-testid="ve-silences-panel">
                            <div className="flex items-center justify-between">
                                <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55">
                                    {silences.length} silent gap{silences.length === 1 ? "" : "s"}
                                </div>
                                <button onClick={trimAllSilences} className="btn-primary text-[10px] px-2 py-1" data-testid="ve-trim-silences">
                                    Trim all
                                </button>
                            </div>
                            <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                                {silences.slice(0, 10).map((s, i) => (
                                    <li key={i} className="flex justify-between">
                                        <span>{s.start.toFixed(2)}s – {s.end.toFixed(2)}s</span>
                                        <span className="text-[var(--text-ink)]/55">{s.duration.toFixed(2)}s</span>
                                    </li>
                                ))}
                                {silences.length > 10 && <li className="text-[var(--text-ink)]/45">…and {silences.length - 10} more</li>}
                            </ul>
                        </div>
                    )}
                    {highlights && highlights.length > 0 && (
                        <div className="card-warm p-3 space-y-2" data-testid="ve-highlights-panel">
                            <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55 inline-flex items-center gap-1">
                                <Sparkles size={12}/> Highlights
                            </div>
                            {highlights.map((h, i) => (
                                <div key={i} className="border rounded-lg p-2 space-y-1" data-testid={`ve-highlight-${i}`}>
                                    <div className="text-xs font-medium">{h.title}</div>
                                    <div className="text-[10px] text-[var(--text-ink)]/55">{h.reason}</div>
                                    <div className="flex items-center justify-between mt-1">
                                        <span className="text-[10px]">{h.start.toFixed(1)}s – {h.end.toFixed(1)}s</span>
                                        <button onClick={() => applyHighlight(h)} className="btn-ghost text-[10px] px-2 py-0.5"
                                                data-testid={`ve-use-highlight-${i}`}>Use this</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Timeline */}
            <div className="card-warm p-3" ref={timelineRef}>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55">Timeline</div>
                    <div className="text-xs text-[var(--text-ink)]/55">
                        {clips.length} clip{clips.length === 1 ? "" : "s"} · ≈ {totalDuration.toFixed(1)}s
                    </div>
                </div>
                {clips.length === 0 ? (
                    <div className="text-sm text-[var(--text-ink)]/50 py-6 text-center" data-testid="ve-timeline-empty">
                        Drop a video above to start your timeline.
                    </div>
                ) : (
                    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="ve-timeline">
                        {clips.map((c, i) => {
                            const input = inputs.find(x => x.id === c.input_id);
                            const dur = Math.max(0.1, (c.end - c.start) / Math.max(0.25, c.speed));
                            return (
                                <button key={c.id} onClick={() => setSelectedClipId(c.id)}
                                        data-testid={`ve-clip-${i}`}
                                        className={`shrink-0 rounded-lg px-3 py-2 border text-left min-w-[140px] ${selectedClipId === c.id ? "border-[var(--brand-magenta)] bg-[rgba(213,25,75,0.06)]" : "border-black/10"}`}>
                                    <div className="text-xs font-medium truncate max-w-[140px]">{input?.filename || c.input_id}</div>
                                    <div className="text-[10px] text-[var(--text-ink)]/55 mt-1">
                                        {c.start.toFixed(1)}–{c.end.toFixed(1)}s · {dur.toFixed(1)}s
                                    </div>
                                </button>
                            );
                        })}
                        <label className="shrink-0 rounded-lg px-3 py-2 border-2 border-dashed border-black/15 text-xs flex items-center gap-1.5 cursor-pointer" data-testid="ve-add-clip">
                            <Plus size={12}/> Add clip
                            <input type="file" accept="video/*" className="hidden" onChange={onUploadVideo}/>
                        </label>
                    </div>
                )}
            </div>
        </div>
    );
}

async function readDuration(file) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => { resolve(isFinite(v.duration) ? v.duration : 60); URL.revokeObjectURL(url); };
        v.onerror = () => { resolve(60); URL.revokeObjectURL(url); };
        v.src = url;
    });
}

function ClipInspector({ clip, input, onChange, onRemove }) {
    return (
        <div className="card-warm p-3 space-y-3" data-testid="ve-clip-inspector">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55">Clip</div>
                <button onClick={onRemove} className="text-xs text-[var(--status-red)] inline-flex items-center gap-1" data-testid="ve-clip-remove">
                    <Trash2 size={12}/> Remove
                </button>
            </div>
            <div className="text-xs truncate">{input?.filename}</div>

            <Slider label="Start" min={0} max={Math.max(1, input?.duration || 60)} step={0.1}
                    value={clip.start} onChange={(v) => onChange({ start: Math.min(v, clip.end - 0.1) })} testid="ve-clip-start"/>
            <Slider label="End" min={0.1} max={input?.duration || 60} step={0.1}
                    value={clip.end} onChange={(v) => onChange({ end: Math.max(v, clip.start + 0.1) })} testid="ve-clip-end"/>
            <Slider label={`Speed ${clip.speed.toFixed(2)}×`} min={0.25} max={4} step={0.05}
                    value={clip.speed} onChange={(v) => onChange({ speed: v })} testid="ve-clip-speed"/>
            <Slider label={`Volume ${(clip.volume * 100).toFixed(0)}%`} min={0} max={2} step={0.05}
                    value={clip.volume} onChange={(v) => onChange({ volume: v })} testid="ve-clip-volume"/>
            <Slider label={`Fade in ${clip.fade_in.toFixed(1)}s`} min={0} max={3} step={0.1}
                    value={clip.fade_in} onChange={(v) => onChange({ fade_in: v })} testid="ve-clip-fadein"/>
            <Slider label={`Fade out ${clip.fade_out.toFixed(1)}s`} min={0} max={3} step={0.1}
                    value={clip.fade_out} onChange={(v) => onChange({ fade_out: v })} testid="ve-clip-fadeout"/>
            <div className="flex items-center justify-between">
                <span className="text-xs">Rotate</span>
                <div className="flex gap-1">
                    {[0, 90, 180, 270].map(d => (
                        <button key={d} onClick={() => onChange({ rotate_deg: d })}
                                data-testid={`ve-clip-rotate-${d}`}
                                className={`text-[10px] px-2 py-1 rounded ${clip.rotate_deg === d ? "brand-gradient text-white" : "btn-ghost"}`}>
                            {d}°
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function AudioTrackRow({ index, t, onChange, onRemove }) {
    return (
        <div className="border-t pt-2 first:border-t-0 first:pt-0 space-y-1.5" data-testid={`ve-audio-track-${index}`}>
            <div className="flex items-center justify-between gap-2">
                <div className="text-xs truncate flex-1"><strong>{index + 1}.</strong> {t.filename}</div>
                <button onClick={onRemove} className="text-xs text-[var(--status-red)]" data-testid={`ve-audio-track-remove-${index}`}>
                    <Trash2 size={12}/>
                </button>
            </div>
            <Slider label={`Volume ${(t.volume * 100).toFixed(0)}%`} min={0} max={1.5} step={0.05}
                    value={t.volume} onChange={(v) => onChange({ volume: v })} testid={`ve-audio-track-vol-${index}`}/>
            <Slider label={`Start offset ${t.start_offset.toFixed(1)}s`} min={0} max={120} step={0.1}
                    value={t.start_offset} onChange={(v) => onChange({ start_offset: v })} testid={`ve-audio-track-offset-${index}`}/>
            <div className="grid grid-cols-2 gap-2">
                <Slider label={`Fade in ${t.fade_in.toFixed(1)}s`} min={0} max={5} step={0.1}
                        value={t.fade_in} onChange={(v) => onChange({ fade_in: v })} testid={`ve-audio-track-fadein-${index}`}/>
                <Slider label={`Fade out ${t.fade_out.toFixed(1)}s`} min={0} max={5} step={0.1}
                        value={t.fade_out} onChange={(v) => onChange({ fade_out: v })} testid={`ve-audio-track-fadeout-${index}`}/>
            </div>
        </div>
    );
}

function AudioInspector({ audio, onChange, onRemove }) {
    return (
        <div className="card-warm p-3 space-y-2" data-testid="ve-audio-inspector">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55 inline-flex items-center gap-1">
                    <Music size={12}/> Music
                </div>
                <button onClick={onRemove} className="text-xs text-[var(--status-red)]" data-testid="ve-audio-remove">
                    <Trash2 size={12}/>
                </button>
            </div>
            <div className="text-xs truncate">{audio.filename}</div>
            <Slider label={`Volume ${(audio.volume * 100).toFixed(0)}%`} min={0} max={1.5} step={0.05}
                    value={audio.volume} onChange={(v) => onChange({ volume: v })} testid="ve-audio-volume"/>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={audio.muteVideo} onChange={(e) => onChange({ muteVideo: e.target.checked })} data-testid="ve-audio-mute-video"/>
                Mute original video audio
            </label>
        </div>
    );
}

function LogoInspector({ logo, onChange, onRemove }) {
    return (
        <div className="card-warm p-3 space-y-2" data-testid="ve-logo-inspector">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-[var(--text-ink)]/55">Logo</div>
                <button onClick={onRemove} className="text-xs text-[var(--status-red)]" data-testid="ve-logo-remove"><Trash2 size={12}/></button>
            </div>
            <div className="text-xs truncate">{logo.filename}</div>
            <Slider label={`Size ${(logo.scale * 100).toFixed(0)}%`} min={0.05} max={0.5} step={0.01}
                    value={logo.scale} onChange={(v) => onChange({ scale: v })} testid="ve-logo-scale"/>
            <div className="grid grid-cols-2 gap-1.5">
                {[
                    { l: "Top-L", x: "30", y: "30" },
                    { l: "Top-R", x: "W-w-30", y: "30" },
                    { l: "Bot-L", x: "30", y: "H-h-30" },
                    { l: "Bot-R", x: "W-w-30", y: "H-h-30" },
                ].map(p => (
                    <button key={p.l} onClick={() => onChange({ x: p.x, y: p.y })}
                            className={`text-[10px] px-2 py-1 rounded ${logo.x === p.x && logo.y === p.y ? "brand-gradient text-white" : "btn-ghost"}`}
                            data-testid={`ve-logo-pos-${p.l}`}>{p.l}</button>
                ))}
            </div>
        </div>
    );
}

function TextOverlayRow({ t, onChange, onRemove }) {
    return (
        <div className="border-t pt-2 first:border-t-0 first:pt-0 space-y-2" data-testid={`ve-text-${t.id}`}>
            <div className="flex items-center justify-between gap-2">
                <input value={t.text} onChange={(e) => onChange({ text: e.target.value })}
                       className="flex-1 px-2 py-1 border rounded text-sm"/>
                <button onClick={onRemove} className="text-xs text-[var(--status-red)]"><Trash2 size={12}/></button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
                <label className="space-y-1">
                    <span className="text-[10px] text-[var(--text-ink)]/55">Start (s)</span>
                    <input type="number" min={0} step={0.1} value={t.start} onChange={(e) => onChange({ start: +e.target.value })} className="w-full px-2 py-1 border rounded"/>
                </label>
                <label className="space-y-1">
                    <span className="text-[10px] text-[var(--text-ink)]/55">End (s)</span>
                    <input type="number" min={0.1} step={0.1} value={t.end ?? 5} onChange={(e) => onChange({ end: +e.target.value })} className="w-full px-2 py-1 border rounded"/>
                </label>
                <label className="space-y-1">
                    <span className="text-[10px] text-[var(--text-ink)]/55">Size</span>
                    <input type="number" min={12} max={200} value={t.fontsize} onChange={(e) => onChange({ fontsize: +e.target.value })} className="w-full px-2 py-1 border rounded"/>
                </label>
                <label className="space-y-1">
                    <span className="text-[10px] text-[var(--text-ink)]/55">Color</span>
                    <input type="color" value={t.color === "white" ? "#ffffff" : t.color} onChange={(e) => onChange({ color: e.target.value })} className="w-full h-7 rounded"/>
                </label>
            </div>
        </div>
    );
}

function Slider({ label, min, max, step = 1, value, onChange, testid }) {
    return (
        <div>
            <div className="text-xs">{label}</div>
            <input type="range" min={min} max={max} step={step} value={value}
                   onChange={(e) => onChange(+e.target.value)} className="w-full" data-testid={testid}/>
        </div>
    );
}

function RenderBanner({ job, onDownload }) {
    const status = job.status || "queued";
    return (
        <div className="card-warm px-3 py-2 mt-3 flex items-center justify-between text-sm" data-testid="ve-render-banner">
            <div className="flex items-center gap-2">
                {["queued", "running"].includes(status) && <Loader2 size={14} className="animate-spin"/>}
                <span>
                    {status === "queued" && "Render queued"}
                    {status === "running" && "Rendering your video…"}
                    {status === "done" && "Render complete"}
                    {status === "failed" && `Render failed${job.error ? `: ${String(job.error).slice(0, 120)}` : ""}`}
                </span>
            </div>
            {status === "done" && (
                <button onClick={onDownload} className="btn-primary text-xs inline-flex items-center gap-1.5" data-testid="ve-download">
                    <Download size={12}/> Download
                </button>
            )}
        </div>
    );
}
